import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Copy, Check, Twitter, Facebook, Mail, 
  MessageSquare, Link2, Lock, Eye, Timer,
  Share2, Users, Trophy, Gamepad2, BookOpen
} from 'lucide-react';
import { sharingService, ShareableContent, ShareLink } from '@/services/sharingService';
import { UserProfile } from '@/types';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareType: ShareableContent['type'];
  shareData: any;
  currentUser: UserProfile;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  shareType,
  shareData,
  currentUser
}) => {
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [shareOptions, setShareOptions] = useState({
    expiresInHours: 0,
    maxViews: 0,
    password: ''
  });

  useEffect(() => {
    if (isOpen && !shareLink) {
      generateShareLink();
    }
  }, [isOpen]);

  const generateShareLink = async () => {
    setIsLoading(true);
    setError(null);

    try {
      let link: ShareLink;

      switch (shareType) {
        case 'progress':
          link = await sharingService.shareProgress(
            currentUser.uid!,
            shareData.gameSessionId,
            shareData.instanceId
          );
          break;

        case 'story_moment':
          link = await sharingService.shareStoryMoment(
            currentUser.uid!,
            shareData.storySegment,
            shareData.choice
          );
          break;

        case 'character':
          link = await sharingService.shareCharacter(currentUser);
          break;

        case 'combat_victory':
          link = await sharingService.shareCombatVictory(
            currentUser.uid!,
            shareData.combatSessionId,
            shareData.combatMode,
            shareData.rewards
          );
          break;

        case 'game_invite':
          link = await sharingService.createGameInvite(
            currentUser.uid!,
            shareData.gameMode,
            shareData.roomId,
            shareData.maxPlayers
          );
          break;

        default:
          throw new Error('Invalid share type');
      }

      setShareLink(link);
    } catch (err) {
      console.error('[ShareModal] Error generating link:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate share link');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!shareLink) return;

    try {
      const url = sharingService.getShareUrl(shareLink);
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[ShareModal] Error copying to clipboard:', err);
    }
  };

  const openSocialShare = (platform: keyof ReturnType<typeof sharingService.getSocialShareUrls>) => {
    if (!shareLink) return;

    // Mock content for social sharing
    const content: ShareableContent = {
      id: shareLink.contentId,
      type: shareType,
      title: getShareTitle(),
      description: getShareDescription(),
      imageUrl: shareData.imageUrl,
      metadata: {},
      createdBy: currentUser.uid!,
      createdAt: new Date()
    };

    const urls = sharingService.getSocialShareUrls(shareLink, content);
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const getShareTitle = () => {
    switch (shareType) {
      case 'progress':
        return `Check out my progress in Lucid Cyber RPG!`;
      case 'story_moment':
        return `Epic moment in Lucid Cyber RPG`;
      case 'character':
        return `My character in Lucid Cyber RPG`;
      case 'combat_victory':
        return `Victory in Lucid Cyber RPG!`;
      case 'game_invite':
        return `Join my game in Lucid Cyber RPG`;
      default:
        return 'Lucid Cyber RPG';
    }
  };

  const getShareDescription = () => {
    switch (shareType) {
      case 'progress':
        return `I'm on Chapter ${shareData.chapter} as a ${currentUser.archetype}!`;
      case 'story_moment':
        return shareData.description || 'An incredible story moment';
      case 'character':
        return `Level ${Math.floor((currentUser.stats?.experience || 0) / 100)} ${currentUser.archetype}`;
      case 'combat_victory':
        return `Won a ${shareData.combatMode} battle!`;
      case 'game_invite':
        return `Playing ${shareData.gameMode} mode - Join now!`;
      default:
        return 'Join the adventure!';
    }
  };

  const getShareIcon = () => {
    switch (shareType) {
      case 'progress':
        return <BookOpen className="w-5 h-5" />;
      case 'story_moment':
        return <BookOpen className="w-5 h-5" />;
      case 'character':
        return <Users className="w-5 h-5" />;
      case 'combat_victory':
        return <Trophy className="w-5 h-5" />;
      case 'game_invite':
        return <Gamepad2 className="w-5 h-5" />;
      default:
        return <Share2 className="w-5 h-5" />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gray-900 rounded-xl border border-cyan-500/30 p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500/20 rounded-lg text-cyan-400">
                  {getShareIcon()}
                </div>
                <h2 className="text-xl font-semibold text-cyan-300">
                  Share {shareType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Content */}
            {isLoading ? (
              <div className="py-12 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-gray-400">Generating share link...</p>
              </div>
            ) : error ? (
              <div className="py-8 text-center">
                <p className="text-red-400 mb-4">{error}</p>
                <button
                  onClick={generateShareLink}
                  className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : shareLink ? (
              <div className="space-y-6">
                {/* Share URL */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Share Link</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={sharingService.getShareUrl(shareLink)}
                      readOnly
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-300"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="px-3 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg transition-colors flex items-center gap-2"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Social Share Buttons */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Share on Social Media</label>
                  <div className="grid grid-cols-4 gap-2">
                    <button
                      onClick={() => openSocialShare('twitter')}
                      className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                      title="Share on Twitter"
                    >
                      <Twitter className="w-5 h-5 text-blue-400" />
                    </button>
                    <button
                      onClick={() => openSocialShare('facebook')}
                      className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                      title="Share on Facebook"
                    >
                      <Facebook className="w-5 h-5 text-blue-600" />
                    </button>
                    <button
                      onClick={() => openSocialShare('discord')}
                      className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                      title="Share on Discord"
                    >
                      <MessageSquare className="w-5 h-5 text-indigo-400" />
                    </button>
                    <button
                      onClick={() => openSocialShare('email')}
                      className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                      title="Share via Email"
                    >
                      <Mail className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Share Options */}
                {shareType === 'game_invite' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Timer className="w-4 h-4" />
                      Expires in 24 hours
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Users className="w-4 h-4" />
                      Max {shareData.maxPlayers} players
                    </div>
                  </div>
                )}

                {/* Link Stats */}
                {shareLink.viewCount > 0 && (
                  <div className="pt-4 border-t border-gray-800">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Eye className="w-4 h-4" />
                      Viewed {shareLink.viewCount} time{shareLink.viewCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};